import { async } from '@angular/core/testing';
import { Injectable } from '@angular/core';
import { Storage } from '../services/storage.service';
import * as _ from 'lodash';
import { JwtHelperService } from '@auth0/angular-jwt';
import * as jwt from 'jwt-decode';
import {StorageCollections} from '../enum/storage-collections.enum';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ConfigService } from '../services/config.service';

@Injectable()
export class TokenStorageService {
  private jwtHelper: JwtHelperService = new JwtHelperService();
  constructor(
    private storage: Storage,
    private config: ConfigService,
    private httpClient: HttpClient
  ) { }

  public async getAccessToken(decoded = false): Promise<string> {
    const rawToken = await this.storage.getItem('access_token');

    if (!rawToken) {
      return null;
    }
    return decoded
      ? jwt(rawToken)
      : rawToken;
  }

  public async isImpersonator() {
    const impersonator = await this.storage.getItem('impersonator');
    return impersonator && Boolean(JSON.parse(impersonator).token);
  }

  public async isSupportUser() {
    let user = await this.storage.getItem("user")
    if (user) {
      user = JSON.parse(user)
      return user.role && user.role.code && user.role.code === 'HC_INTERNAL_CUST_SUPPORT';
    }
    return false;
  }

  public async getImpersonator(): Promise<string> {
    return await this.storage.getItem('impersonator');
  }

  public async setAccessToken(token: string) {
    return await this.storage.setItem('access_token', token);
  }

  public async hasAccessToken(): Promise<boolean> {
    try {
      const accessToken = await this.getAccessToken();
      return Promise.resolve(!_.isEmpty(accessToken));
    } catch {
      return Promise.resolve(false);
    }
  }

  public async hasRefreshToken(): Promise<boolean> {
    try {
      const refreshToken = await this.getRefreshToken();
      return Promise.resolve(!_.isEmpty(refreshToken));
    } catch {
      return Promise.resolve(false);
    }
  }

  public async getRefreshToken(): Promise<string> {
    return await this.storage.getItem('refresh_token');
  }

  public async setRefreshToken(token: string) {
    return await this.storage.setItem('refresh_token', token);
  }

  public async clearTokens() {
    return await Promise.all([
      this.storage.removeItem(StorageCollections.SECURITY, 'access_token'),
      this.storage.removeItem(StorageCollections.SECURITY, 'refresh_token')
  ]);

  }

  public async clearUserContext() {
    return await Promise.all([
      this.storage.removeObjectStore(StorageCollections.SECURITY),
      this.storage.removeObjectStore(StorageCollections.CONFIG),
      this.storage.removeObjectStore(StorageCollections.HUBCONNECT)
    ]);
  }

  public async hasValidAccessToken(): Promise<boolean> {
    return (
      await this.hasAccessToken() &&
      await !this.isTokenExpired()
    );
  }

  private async isTokenExpired() {
    return this.jwtHelper.isTokenExpired(await this.getAccessToken());
  }

  public tokenExpired() {
    return false;
  }

  public async validateSession(): Promise<boolean> {
    if (
      await this.hasAccessToken() &&
      await this.storage.getItem('user')
    ) {
      return true;
    } else {
      return false;
    }
  }

  public async isMultiProfiler() {
    const selectedProfile = await this.storage.getItem('selected_profile');
    return selectedProfile && !_.isEmpty(selectedProfile);
  }

  public async getProfileToken(): Promise<any> {
    const config = await this.config.getConfig();
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      client_id: config.runtime.authClientId
    });
    let b2cToken = await this.storage.getItem('b2c_token');
    let organizationType = await this.storage.getItem('selected_profile');
    return this.httpClient.post(`${config.runtime.authBaseUrl}/azure-ad-b2c/jwt`, { token: b2cToken, organizationType: organizationType }, { headers: headers })
      .toPromise()
      .then(async (response: any) => {
        let tokenExpiration = Date.now() + response.expires_in * 1000;
        await this.storage.setItem('access_token', response.access_token);
        await this.storage.setItem('refresh_token', response.refresh_token);
        await this.storage.setItem('expire_time', tokenExpiration);
        return response;
      }).catch((error) => {
        console.error(error);
      });
  }
}
